/**
 * Adapted from PlayCanvas Engine's gsplat-compressed-data.js at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */
import * as THREE from 'three'
import { GSplatData } from '@/plugins/gsplat/core/GSplatData'

const SPLATS_PER_CHUNK = 256
const SH_C0 = 0.28209479177387814
const SH_COEFFICIENTS = [3, 8, 15] as const

/** 旧 createIterator/getSplat façade 返回的 PLY-space 属性。 */
export interface SplatAttributes {
  readonly position: [number, number, number]
  /** [w, x, y, z] */
  readonly rotation: [number, number, number, number]
  /** PLY log-scale。 */
  readonly scale: [number, number, number]
  /** PLY SH DC coefficient，而不是激活后的 RGB。 */
  readonly color: [number, number, number]
  /** PLY pre-sigmoid opacity。 */
  readonly opacity: number
  /** 旧 façade 保持实际 band 的紧凑 channel-major 数组。 */
  readonly sh?: Float32Array
}

export interface GSplatCompressedDataParams {
  readonly numSplats: number
  readonly numChunks: number
  readonly chunkSize: number
  readonly chunkData: Float32Array
  readonly vertexData: Uint32Array
  readonly hasColorRange: boolean
  readonly shBands: number
  readonly comments?: string[]
  readonly shData0?: Uint8Array | null
  readonly shData1?: Uint8Array | null
  readonly shData2?: Uint8Array | null
}

/**
 * 冻结 PlayCanvas `SplatCompressedIterator` 的 Three.js 适配。
 * `read` 只写调用方提供的对象；颜色为激活后的 RGBA，SH 固定为 3*15 且高阶清零。
 */
export class SplatCompressedIterator {
  private readonly data: GSplatCompressedData
  private readonly shCoefficients: number

  constructor(
    data: GSplatCompressedData,
    private readonly position?: THREE.Vector3,
    private readonly rotation?: THREE.Quaternion,
    private readonly scale?: THREE.Vector3,
    private readonly color?: THREE.Vector4,
    private readonly sh?: Float32Array
  ) {
    this.data = data
    this.shCoefficients = data.shBands > 0 ? SH_COEFFICIENTS[data.shBands - 1] : 0
    if (sh && sh.length < 45) {
      throw new Error(`SplatCompressedIterator: SH 输出长度不足，expected>=45, actual=${sh.length}`)
    }
  }

  read(index: number): void {
    const data = this.data
    const chunkOffset = Math.floor(index / SPLATS_PER_CHUNK) * data.chunkSize
    const vertexOffset = index * 4

    if (this.position) {
      unpack111011(this.position, data.vertexData[vertexOffset])
      this.position.x = lerp(
        data.chunkData[chunkOffset],
        data.chunkData[chunkOffset + 3],
        this.position.x
      )
      this.position.y = lerp(
        data.chunkData[chunkOffset + 1],
        data.chunkData[chunkOffset + 4],
        this.position.y
      )
      this.position.z = lerp(
        data.chunkData[chunkOffset + 2],
        data.chunkData[chunkOffset + 5],
        this.position.z
      )
    }

    if (this.rotation) unpackRotation(this.rotation, data.vertexData[vertexOffset + 1])

    if (this.scale) {
      unpack111011(this.scale, data.vertexData[vertexOffset + 2])
      this.scale.x = lerp(
        data.chunkData[chunkOffset + 6],
        data.chunkData[chunkOffset + 9],
        this.scale.x
      )
      this.scale.y = lerp(
        data.chunkData[chunkOffset + 7],
        data.chunkData[chunkOffset + 10],
        this.scale.y
      )
      this.scale.z = lerp(
        data.chunkData[chunkOffset + 8],
        data.chunkData[chunkOffset + 11],
        this.scale.z
      )
    }

    if (this.color) {
      unpack8888(this.color, data.vertexData[vertexOffset + 3])
      if (data.chunkSize > 12) {
        this.color.x = lerp(
          data.chunkData[chunkOffset + 12],
          data.chunkData[chunkOffset + 15],
          this.color.x
        )
        this.color.y = lerp(
          data.chunkData[chunkOffset + 13],
          data.chunkData[chunkOffset + 16],
          this.color.y
        )
        this.color.z = lerp(
          data.chunkData[chunkOffset + 14],
          data.chunkData[chunkOffset + 17],
          this.color.z
        )
      }
    }

    if (this.sh && data.shBands > 0) {
      const sources = [data.shData0!, data.shData1!, data.shData2!]
      for (let channel = 0; channel < 3; channel++) {
        for (let coefficient = 0; coefficient < 15; coefficient++) {
          this.sh[channel * 15 + coefficient] =
            coefficient < this.shCoefficients
              ? sources[channel][index * 16 + coefficient] * (8 / 255) - 4
              : 0
        }
      }
    }
  }
}

/** PlayCanvas compressed PLY data model；GPU Resource 只借用这些不可变 buffer。 */
export class GSplatCompressedData {
  readonly numSplats: number
  readonly numChunks: number
  readonly chunkSize: number
  readonly hasColorRange: boolean
  readonly shBands: number
  readonly comments: string[]
  readonly chunkData: Float32Array
  readonly vertexData: Uint32Array
  readonly shData0: Uint8Array | null
  readonly shData1: Uint8Array | null
  readonly shData2: Uint8Array | null

  constructor(params: GSplatCompressedDataParams) {
    validateCompressedParams(params)
    this.numSplats = params.numSplats
    this.numChunks = params.numChunks
    this.chunkSize = params.chunkSize
    this.chunkData = params.chunkData
    this.vertexData = params.vertexData
    this.hasColorRange = params.hasColorRange
    this.shBands = params.shBands
    this.comments = params.comments ?? []
    this.shData0 = params.shData0 ?? null
    this.shData1 = params.shData1 ?? null
    this.shData2 = params.shData2 ?? null
  }

  createIter(
    position?: THREE.Vector3,
    rotation?: THREE.Quaternion,
    scale?: THREE.Vector3,
    color?: THREE.Vector4,
    sh?: Float32Array
  ): SplatCompressedIterator {
    return new SplatCompressedIterator(this, position, rotation, scale, color, sh)
  }

  /** 旧调用方兼容 façade；返回 PLY-space tuple。 */
  createIterator(): CompressedIterator {
    return new CompressedIterator(this)
  }

  getSplat(index: number): SplatAttributes {
    return this.createIterator().read(index)
  }

  getCenters(): Float32Array {
    const result = new Float32Array(this.numSplats * 3)
    for (let chunkIndex = 0; chunkIndex < this.numChunks; chunkIndex++) {
      const chunkOffset = chunkIndex * this.chunkSize
      const minX = this.chunkData[chunkOffset]
      const minY = this.chunkData[chunkOffset + 1]
      const minZ = this.chunkData[chunkOffset + 2]
      const maxX = this.chunkData[chunkOffset + 3]
      const maxY = this.chunkData[chunkOffset + 4]
      const maxZ = this.chunkData[chunkOffset + 5]
      const end = Math.min(this.numSplats, (chunkIndex + 1) * SPLATS_PER_CHUNK)
      for (let index = chunkIndex * SPLATS_PER_CHUNK; index < end; index++) {
        const packed = this.vertexData[index * 4]
        const x = (packed >>> 21) / 2047
        const y = ((packed >>> 11) & 0x3ff) / 1023
        const z = (packed & 0x7ff) / 2047
        result[index * 3] = lerp(minX, maxX, x)
        result[index * 3 + 1] = lerp(minY, maxY, y)
        result[index * 3 + 2] = lerp(minZ, maxZ, z)
      }
    }
    return result
  }

  calcAabb(result: THREE.Box3): boolean {
    if (this.numChunks === 0) return false
    let radius = Math.exp(Math.max(this.chunkData[9], this.chunkData[10], this.chunkData[11]))
    let minX = this.chunkData[0] - radius
    let minY = this.chunkData[1] - radius
    let minZ = this.chunkData[2] - radius
    let maxX = this.chunkData[3] + radius
    let maxY = this.chunkData[4] + radius
    let maxZ = this.chunkData[5] + radius
    for (let index = 1; index < this.numChunks; index++) {
      const offset = index * this.chunkSize
      radius = Math.exp(
        Math.max(
          this.chunkData[offset + 9],
          this.chunkData[offset + 10],
          this.chunkData[offset + 11]
        )
      )
      minX = Math.min(minX, this.chunkData[offset] - radius)
      minY = Math.min(minY, this.chunkData[offset + 1] - radius)
      minZ = Math.min(minZ, this.chunkData[offset + 2] - radius)
      maxX = Math.max(maxX, this.chunkData[offset + 3] + radius)
      maxY = Math.max(maxY, this.chunkData[offset + 4] + radius)
      maxZ = Math.max(maxZ, this.chunkData[offset + 5] + radius)
    }
    result.min.set(minX, minY, minZ)
    result.max.set(maxX, maxY, maxZ)
    return true
  }

  getChunks(result: Float32Array): void {
    const required = this.numChunks * 6
    if (result.length < required) {
      throw new Error(
        `CompressedSplatData.getChunks: 输出长度不足，expected>=${required}, actual=${result.length}`
      )
    }
    for (let index = 0; index < this.numChunks; index++) {
      const sourceOffset = index * this.chunkSize
      const targetOffset = index * 6
      result[targetOffset] = this.chunkData[sourceOffset]
      result[targetOffset + 1] = this.chunkData[sourceOffset + 1]
      result[targetOffset + 2] = this.chunkData[sourceOffset + 2]
      result[targetOffset + 3] = this.chunkData[sourceOffset + 3]
      result[targetOffset + 4] = this.chunkData[sourceOffset + 4]
      result[targetOffset + 5] = this.chunkData[sourceOffset + 5]
    }
  }

  calcFocalPoint(result: THREE.Vector3): void {
    result.set(0, 0, 0)
    for (let index = 0; index < this.numChunks; index++) {
      const offset = index * this.chunkSize
      result.x += this.chunkData[offset] + this.chunkData[offset + 3]
      result.y += this.chunkData[offset + 1] + this.chunkData[offset + 4]
      result.z += this.chunkData[offset + 2] + this.chunkData[offset + 5]
    }
    result.multiplyScalar(0.5 / this.numChunks)
  }

  get isCompressed(): boolean {
    return true
  }

  decompress(): GSplatData {
    const members = [
      'x',
      'y',
      'z',
      'f_dc_0',
      'f_dc_1',
      'f_dc_2',
      'opacity',
      'scale_0',
      'scale_1',
      'scale_2',
      'rot_0',
      'rot_1',
      'rot_2',
      'rot_3',
    ]
    if (this.shBands > 0) {
      const shMembers = Array.from({ length: 45 }, (_, index) => `f_rest_${index}`)
      members.splice(members.indexOf('f_dc_2') + 1, 0, ...shMembers)
    }
    const storage = Object.fromEntries(
      members.map((name) => [name, new Float32Array(this.numSplats)])
    ) as Record<string, Float32Array>
    const position = new THREE.Vector3()
    const rotation = new THREE.Quaternion()
    const scale = new THREE.Vector3()
    const color = new THREE.Vector4()
    const sh = this.shBands > 0 ? new Float32Array(45) : undefined
    const iter = this.createIter(position, rotation, scale, color, sh)

    for (let index = 0; index < this.numSplats; index++) {
      iter.read(index)
      storage.x[index] = position.x
      storage.y[index] = position.y
      storage.z[index] = position.z
      storage.rot_0[index] = rotation.w
      storage.rot_1[index] = rotation.x
      storage.rot_2[index] = rotation.y
      storage.rot_3[index] = rotation.z
      storage.scale_0[index] = scale.x
      storage.scale_1[index] = scale.y
      storage.scale_2[index] = scale.z
      storage.f_dc_0[index] = (color.x - 0.5) / SH_C0
      storage.f_dc_1[index] = (color.y - 0.5) / SH_C0
      storage.f_dc_2[index] = (color.z - 0.5) / SH_C0
      storage.opacity[index] = color.w <= 0 ? -40 : color.w >= 1 ? 40 : -Math.log(1 / color.w - 1)
      if (sh) {
        for (let coefficient = 0; coefficient < 45; coefficient++) {
          storage[`f_rest_${coefficient}`][index] = sh[coefficient]
        }
      }
    }

    return new GSplatData(
      [
        {
          name: 'vertex',
          count: this.numSplats,
          properties: members.map((name) => ({
            name,
            type: 'float',
            byteSize: 4,
            storage: storage[name],
          })),
        },
      ],
      this.comments
    )
  }

  /** 本地 octree 兼容：按解压后的中心计算稳定 Morton 顺序。 */
  calcMortonOrder(): Uint32Array {
    const centers = this.getCenters()
    if (this.numSplats === 0) return new Uint32Array()
    const axes = [0, 1, 2].map((axis) => {
      let min = centers[axis]
      let max = centers[axis]
      for (let index = 1; index < this.numSplats; index++) {
        const value = centers[index * 3 + axis]
        if (value < min) min = value
        if (value > max) max = value
      }
      return { min, scale: min === max ? 0 : 1024 / (max - min) }
    })
    const codes = new Map<number, number[]>()
    for (let index = 0; index < this.numSplats; index++) {
      const x = Math.min(1023, Math.floor((centers[index * 3] - axes[0].min) * axes[0].scale))
      const y = Math.min(1023, Math.floor((centers[index * 3 + 1] - axes[1].min) * axes[1].scale))
      const z = Math.min(1023, Math.floor((centers[index * 3 + 2] - axes[2].min) * axes[2].scale))
      const code = encodeMorton3D(x, y, z)
      const bucket = codes.get(code)
      if (bucket) bucket.push(index)
      else codes.set(code, [index])
    }
    const result = new Uint32Array(this.numSplats)
    let target = 0
    for (const code of [...codes.keys()].sort((left, right) => left - right)) {
      for (const index of codes.get(code)!) result[target++] = index
    }
    return result
  }

  getMemoryUsage(): number {
    return (
      this.chunkData.byteLength +
      this.vertexData.byteLength +
      (this.shData0?.byteLength ?? 0) +
      (this.shData1?.byteLength ?? 0) +
      (this.shData2?.byteLength ?? 0)
    )
  }

  getUncompressedMemoryUsage(): number {
    const shSize = this.shBands > 0 ? [36, 96, 180][this.shBands - 1] : 0
    return this.numSplats * (56 + shSize)
  }

  getCompressionRatio(): number {
    return this.getUncompressedMemoryUsage() / this.getMemoryUsage()
  }
}

/** 旧 tuple API；内部委托上游同形 iterator，不复制解码公式。 */
export class CompressedIterator {
  private readonly position = new THREE.Vector3()
  private readonly rotation = new THREE.Quaternion()
  private readonly scale = new THREE.Vector3()
  private readonly color = new THREE.Vector4()
  private readonly fullSh?: Float32Array
  private readonly iterator: SplatCompressedIterator

  constructor(private readonly data: GSplatCompressedData) {
    this.fullSh = data.shBands > 0 ? new Float32Array(45) : undefined
    this.iterator = data.createIter(
      this.position,
      this.rotation,
      this.scale,
      this.color,
      this.fullSh
    )
  }

  read(index: number): SplatAttributes {
    this.iterator.read(index)
    let sh: Float32Array | undefined
    if (this.fullSh) {
      const coefficients = SH_COEFFICIENTS[this.data.shBands - 1]
      sh = new Float32Array(coefficients * 3)
      for (let channel = 0; channel < 3; channel++) {
        for (let coefficient = 0; coefficient < coefficients; coefficient++) {
          sh[channel * coefficients + coefficient] = this.fullSh[channel * 15 + coefficient]
        }
      }
    }
    return {
      position: [this.position.x, this.position.y, this.position.z],
      rotation: [this.rotation.w, this.rotation.x, this.rotation.y, this.rotation.z],
      scale: [this.scale.x, this.scale.y, this.scale.z],
      color: [
        (this.color.x - 0.5) / SH_C0,
        (this.color.y - 0.5) / SH_C0,
        (this.color.z - 0.5) / SH_C0,
      ],
      opacity: this.color.w <= 0 ? -40 : this.color.w >= 1 ? 40 : -Math.log(1 / this.color.w - 1),
      ...(sh ? { sh } : {}),
    }
  }
}

function validateCompressedParams(params: GSplatCompressedDataParams): void {
  if (!Number.isSafeInteger(params.numSplats) || params.numSplats < 0) {
    throw new Error(`CompressedSplatData: 非法 numSplats=${params.numSplats}`)
  }
  const expectedChunks = Math.ceil(params.numSplats / SPLATS_PER_CHUNK)
  if (params.numChunks !== expectedChunks) {
    throw new Error(
      `CompressedSplatData: numChunks 不匹配，expected=${expectedChunks}, actual=${params.numChunks}`
    )
  }
  if (params.chunkSize !== 12 && params.chunkSize !== 18) {
    throw new Error(`CompressedSplatData: chunkSize 必须为 12 或 18，actual=${params.chunkSize}`)
  }
  if (params.hasColorRange !== (params.chunkSize === 18)) {
    throw new Error('CompressedSplatData: hasColorRange 与 chunkSize 不一致')
  }
  if (params.chunkData.length !== params.numChunks * params.chunkSize) {
    throw new Error(
      `CompressedSplatData: chunkData 长度不匹配，expected=${params.numChunks * params.chunkSize}, actual=${params.chunkData.length}`
    )
  }
  if (params.vertexData.length !== params.numSplats * 4) {
    throw new Error(
      `CompressedSplatData: vertexData 长度不匹配，expected=${params.numSplats * 4}, actual=${params.vertexData.length}`
    )
  }
  if (!Number.isSafeInteger(params.shBands) || params.shBands < 0 || params.shBands > 3) {
    throw new Error(`CompressedSplatData: 非法 shBands=${params.shBands}`)
  }
  const shBuffers = [params.shData0 ?? null, params.shData1 ?? null, params.shData2 ?? null]
  if (params.shBands === 0) {
    if (shBuffers.some(Boolean))
      throw new Error('CompressedSplatData: shBands=0 时不得提供 SH buffer')
    return
  }
  const expectedShLength = params.numSplats * 16
  for (let channel = 0; channel < shBuffers.length; channel++) {
    if (!shBuffers[channel] || shBuffers[channel]!.length !== expectedShLength) {
      throw new Error(
        `CompressedSplatData: shData${channel} 长度不匹配，expected=${expectedShLength}, actual=${shBuffers[channel]?.length ?? 0}`
      )
    }
  }
}

function unpackUnorm(value: number, bits: number): number {
  const max = (1 << bits) - 1
  return (value & max) / max
}

function unpack111011(result: THREE.Vector3, value: number): void {
  result.set(unpackUnorm(value >>> 21, 11), unpackUnorm(value >>> 11, 10), unpackUnorm(value, 11))
}

function unpack8888(result: THREE.Vector4, value: number): void {
  result.set(
    unpackUnorm(value >>> 24, 8),
    unpackUnorm(value >>> 16, 8),
    unpackUnorm(value >>> 8, 8),
    unpackUnorm(value, 8)
  )
}

function unpackRotation(result: THREE.Quaternion, value: number): void {
  const a = (unpackUnorm(value >>> 20, 10) - 0.5) * Math.SQRT2
  const b = (unpackUnorm(value >>> 10, 10) - 0.5) * Math.SQRT2
  const c = (unpackUnorm(value, 10) - 0.5) * Math.SQRT2
  // 与冻结上游一致：不额外 clamp radicand，坏 packed 值应显式产生 NaN。
  const missing = Math.sqrt(1 - (a * a + b * b + c * c))
  switch (value >>> 30) {
    case 0:
      result.set(a, b, c, missing)
      break
    case 1:
      result.set(missing, b, c, a)
      break
    case 2:
      result.set(b, missing, c, a)
      break
    case 3:
      result.set(b, c, missing, a)
      break
  }
}

function lerp(left: number, right: number, amount: number): number {
  return left * (1 - amount) + right * amount
}

function encodeMorton3D(x: number, y: number, z: number): number {
  const part1By2 = (value: number): number => {
    value &= 0x000003ff
    value = (value ^ (value << 16)) & 0xff0000ff
    value = (value ^ (value << 8)) & 0x0300f00f
    value = (value ^ (value << 4)) & 0x030c30c3
    value = (value ^ (value << 2)) & 0x09249249
    return value
  }
  return (part1By2(z) << 2) + (part1By2(y) << 1) + part1By2(x)
}
